import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageSquare, Plus, X, Send, Users, ArrowLeft, UserPlus, Search, Image, Paperclip, Check, CheckCheck, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReadReceipt = ({ message, currentUserId, totalMembers }) => {
  if (message.sender_id !== currentUserId) return null;
  const readCount = (message.read_by || []).filter(id => id !== currentUserId).length;
  const allRead = readCount >= totalMembers - 1 && totalMembers > 1;
  const names = message.read_by_names || [];

  return (
    <div className="flex items-center justify-end mt-0.5 space-x-0.5" data-testid={`read-receipt-${message.id}`}>
      {allRead ? (
        <CheckCheck className="w-3.5 h-3.5 text-blue-400" />
      ) : readCount > 0 ? (
        <CheckCheck className="w-3.5 h-3.5 text-gray-400" />
      ) : (
        <Check className="w-3.5 h-3.5 text-gray-400" />
      )}
      {names.length > 0 && (
        <span className="text-[10px] text-gray-500 ml-1">
          {names.length <= 2 ? names.join(', ') : `${names[0]} +${names.length - 1}`}
        </span>
      )}
    </div>
  );
};

export default function ChatGroups() {
  const { groupId } = useParams();
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [connections, setConnections] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const typingPollRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const lastTypingSent = useRef(0);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups`, { headers: { Authorization: `Bearer ${token}` } });
      setGroups(res.data);
    } catch (err) {
      console.error('Failed to fetch groups:', err.message);
    }
    setLoading(false);
  }, [token]);

  const fetchMessages = useCallback(async (gid) => {
    try {
      const res = await axios.get(`${API}/groups/${gid}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(res.data);
    } catch (err) {
      console.error('Failed to fetch messages:', err.message);
    }
  }, [token]);

  const fetchConnections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/connections`, { headers: { Authorization: `Bearer ${token}` } });
      const users = [];
      for (const conn of res.data) {
        const otherId = conn.user_id_1 === user.id ? conn.user_id_2 : conn.user_id_1;
        try {
          const u = await axios.get(`${API}/users/${otherId}`, { headers: { Authorization: `Bearer ${token}` } });
          users.push({ id: otherId, name: u.data.name, photo: u.data.profile_photo });
        } catch (e) {
          console.error('Failed to fetch user:', otherId);
        }
      }
      setConnections(users);
    } catch (err) {
      console.error('Failed to fetch connections:', err.message);
    }
  }, [token, user?.id]);

  useEffect(() => {
    fetchGroups();
    fetchConnections();
  }, [fetchGroups, fetchConnections]);

  useEffect(() => { if (groupId) openGroup(groupId); }, [groupId]); // eslint-disable-line

  // Message + typing polling
  useEffect(() => {
    if (activeGroup) {
      pollRef.current = setInterval(() => fetchMessages(activeGroup.id), 3000);
      typingPollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/groups/${activeGroup.id}/typing`, { headers: { Authorization: `Bearer ${token}` } });
          setTypingUsers(res.data);
        } catch (e) {
          console.error('Typing poll error:', e.message);
        }
      }, 2000);
      return () => {
        clearInterval(pollRef.current);
        clearInterval(typingPollRef.current);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      };
    }
  }, [activeGroup, fetchMessages]);

  const prevMessageCountRef = useRef(0);

  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  const openGroup = async (gid) => {
    try {
      const res = await axios.get(`${API}/groups/${gid}`, { headers: authHeaders() });
      setActiveGroup(res.data);
      fetchMessages(gid);
    } catch (err) {
      toast.error('Group not found');
    }
  };

  const createGroup = async () => {
    if (!groupName.trim()) { toast.error('Enter a group name'); return; }
    try {
      const res = await axios.post(`${API}/groups/create`, { name: groupName.trim() }, { headers: authHeaders() });
      toast.success('Group created!');
      setGroupName('');
      setShowCreate(false);
      setActiveGroup(res.data);
      fetchGroups();
    } catch (err) {
      toast.error('Failed to create group');
    }
  };

  // Typing indicator
  const sendTyping = () => {
    if (!activeGroup) return;
    const now = Date.now();
    if (now - lastTypingSent.current < 3000) return;
    lastTypingSent.current = now;
    axios.post(`${API}/groups/${activeGroup.id}/typing`, { is_typing: true }, { headers: authHeaders() }).catch((e) => console.debug('Typing send error:', e.message));
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      axios.post(`${API}/groups/${activeGroup.id}/typing`, { is_typing: false }, { headers: authHeaders() }).catch((e) => console.debug('Typing clear error:', e.message));
    }, 5000);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !activeGroup) return;
    setUploading(true);
    const urls = [];
    for (const file of files) {
      try {
        const form = new FormData();
        form.append('file', file);
        const res = await axios.post(`${API}/groups/${activeGroup.id}/upload`, form, { headers: authHeaders() });
        urls.push(res.data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setPendingImages(prev => [...prev, ...urls]);
    setUploading(false);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !activeGroup) return;
    setUploading(true);
    const fileInfos = [];
    for (const file of files) {
      try {
        const form = new FormData();
        form.append('file', file);
        const res = await axios.post(`${API}/groups/${activeGroup.id}/upload`, form, { headers: authHeaders() });
        fileInfos.push({ name: res.data.name, url: res.data.url, size: res.data.size, type: res.data.type });
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setPendingFiles(prev => [...prev, ...fileInfos]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && !pendingImages.length && !pendingFiles.length) || !activeGroup) return;
    try {
      await axios.post(`${API}/groups/${activeGroup.id}/message`, {
        content: newMessage.trim(),
        images: pendingImages,
        files: pendingFiles
      }, { headers: authHeaders() });
      setNewMessage('');
      setPendingImages([]);
      setPendingFiles([]);
      fetchMessages(activeGroup.id);
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  const addMember = async (memberId) => {
    try {
      await axios.post(`${API}/groups/${activeGroup.id}/add-member?member_id=${memberId}`, {}, { headers: authHeaders() });
      toast.success('Member added');
      openGroup(activeGroup.id);
      setShowAddMember(false);
    } catch (err) { toast.error('Failed to add member'); }
  };

  const leaveGroup = async () => {
    if (!activeGroup) return;
    try {
      await axios.post(`${API}/groups/${activeGroup.id}/leave`, {}, { headers: authHeaders() });
      toast.success('Left group');
      setActiveGroup(null);
      fetchGroups();
    } catch (err) { toast.error('Failed to leave group'); }
  };

  const filteredConnections = connections.filter(c => {
    if (activeGroup && activeGroup.members?.includes(c.id)) return false;
    if (!searchQuery) return true;
    return c.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const renderMessage = (msg) => {
    const isMine = msg.sender_id === user.id;
    return (
      <div key={msg.id} data-testid={`group-msg-${msg.id}`} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[75%] ${isMine ? '' : 'flex space-x-2'}`}>
          {!isMine && (
            <Avatar className="w-7 h-7 flex-shrink-0 mt-1">
              <AvatarImage src={msg.sender_photo} />
              <AvatarFallback className="bg-teal text-white text-xs">{msg.sender_name?.charAt(0)}</AvatarFallback>
            </Avatar>
          )}
          <div>
            {!isMine && <p className="text-xs text-gray-500 mb-0.5 ml-1">{msg.sender_name}</p>}
            <div className={`px-3 py-2 rounded-2xl text-sm ${isMine ? 'bg-coral text-white rounded-br-md' : 'bg-gray-100 text-gray-900 rounded-bl-md'}`}>
              {msg.content && <p>{msg.content}</p>}
              {msg.images?.length > 0 && (
                <div className={`${msg.content ? 'mt-2' : ''} grid gap-1 ${msg.images.length > 1 ? 'grid-cols-2' : ''}`}>
                  {msg.images.map((img, i) => (
                    <a key={`img-${msg.id}-${i}`} href={img} target="_blank" rel="noopener noreferrer">
                      <img src={img} alt="" className="rounded-lg max-h-48 w-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    </a>
                  ))}
                </div>
              )}
              {msg.files?.length > 0 && (
                <div className={`${msg.content || msg.images?.length ? 'mt-2' : ''} space-y-1`}>
                  {msg.files.map((f, i) => (
                    <a key={`file-${msg.id}-${i}`} href={f.url} target="_blank" rel="noopener noreferrer"
                      className={`flex items-center space-x-2 p-1.5 rounded-lg ${isMine ? 'bg-white/20 hover:bg-white/30' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}>
                      <FileText className="w-4 h-4 flex-shrink-0" />
                      <span className="text-xs truncate">{f.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-[10px] text-gray-400 mt-0.5 ml-1">
                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
              <ReadReceipt message={msg} currentUserId={user.id} totalMembers={activeGroup?.members?.length || 0} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-teal-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 pt-20 pb-4 h-screen flex flex-col">
        <div className="flex-1 flex bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100" style={{ maxHeight: 'calc(100vh - 100px)' }}>
          {/* Sidebar */}
          <div className={`w-80 border-r border-gray-100 flex flex-col ${activeGroup ? 'hidden sm:flex' : 'flex'}`}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-lg">Group Chats</h2>
              <Button size="sm" onClick={() => setShowCreate(true)} className="bg-coral hover:bg-coral/90 text-white rounded-full" data-testid="create-group-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              {groups.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No group chats yet</p>
                </div>
              ) : groups.map(g => (
                <div key={g.id} data-testid={`group-item-${g.id}`}
                  onClick={() => { setActiveGroup(g); fetchMessages(g.id); navigate(`/groups/${g.id}`); }}
                  className={`p-3 flex items-center space-x-3 cursor-pointer hover:bg-gray-50 border-b border-gray-50 ${activeGroup?.id === g.id ? 'bg-teal/5' : ''}`}>
                  <div className="w-10 h-10 rounded-full bg-coral/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-coral" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-sm truncate">{g.name}</h3>
                      {g.unread_count > 0 && <span className="bg-coral text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{g.unread_count}</span>}
                    </div>
                    <p className="text-xs text-gray-400 truncate">{g.last_message || `${g.members?.length || 0} members`}</p>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col ${!activeGroup ? 'hidden sm:flex' : 'flex'}`}>
            {activeGroup ? (
              <>
                <div className="p-3 border-b border-gray-100 flex items-center space-x-3">
                  <Button variant="ghost" size="sm" onClick={() => { setActiveGroup(null); navigate('/groups'); }} className="sm:hidden">
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="w-9 h-9 rounded-full bg-coral/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-coral" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm">{activeGroup.name}</h3>
                    <p className="text-xs text-gray-400">{activeGroup.members?.length} members</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setShowAddMember(true); setSearchQuery(''); }} data-testid="add-member-btn">
                    <UserPlus className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={leaveGroup} className="text-red-500 hover:bg-red-50 text-xs">Leave</Button>
                </div>

                <ScrollArea className="flex-1 p-4" data-testid="group-messages-area">
                  <div className="space-y-3">
                    {messages.map(renderMessage)}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Typing indicator */}
                {typingUsers.length > 0 && (
                  <div className="px-4 py-1" data-testid="typing-indicator">
                    <p className="text-xs text-gray-500 italic animate-pulse">
                      {typingUsers.length === 1
                        ? `${typingUsers[0].name} is typing...`
                        : typingUsers.length === 2
                        ? `${typingUsers[0].name} and ${typingUsers[1].name} are typing...`
                        : `${typingUsers[0].name} and ${typingUsers.length - 1} others are typing...`}
                    </p>
                  </div>
                )}

                {/* Pending attachments preview */}
                {(pendingImages.length > 0 || pendingFiles.length > 0) && (
                  <div className="px-3 py-2 border-t border-gray-50 flex flex-wrap gap-2" data-testid="pending-attachments">
                    {pendingImages.map((img, i) => (
                      <div key={`img-${i}`} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setPendingImages(prev => prev.filter((_, j) => j !== i))}
                          className="absolute top-0 right-0 bg-black/60 rounded-bl-lg p-0.5"><X className="w-3 h-3 text-white" /></button>
                      </div>
                    ))}
                    {pendingFiles.map((f, i) => (
                      <div key={`file-${i}`} className="flex items-center space-x-1 bg-gray-100 rounded-lg px-2 py-1 text-xs">
                        <FileText className="w-3 h-3" />
                        <span className="max-w-[80px] truncate">{f.name}</span>
                        <button onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}><X className="w-3 h-3 text-gray-500" /></button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Input bar */}
                <div className="p-3 border-t border-gray-100 flex items-center space-x-2">
                  <input ref={imageInputRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                  <input ref={fileInputRef} type="file" onChange={handleFileUpload} className="hidden" />
                  <button onClick={() => imageInputRef.current?.click()} disabled={uploading}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-500 disabled:opacity-50" data-testid="attach-image-btn" title="Send photo">
                    <Image className="w-5 h-5" />
                  </button>
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-500 disabled:opacity-50" data-testid="attach-file-btn" title="Send file">
                    <Paperclip className="w-5 h-5" />
                  </button>
                  <Input
                    data-testid="group-message-input"
                    value={newMessage}
                    onChange={(e) => { setNewMessage(e.target.value); sendTyping(); }}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    placeholder={uploading ? 'Uploading...' : 'Type a message...'}
                    disabled={uploading}
                    className="flex-1 rounded-full"
                  />
                  <Button onClick={sendMessage} size="sm" disabled={uploading}
                    className="bg-coral hover:bg-coral/90 text-white rounded-full p-2" data-testid="send-group-msg-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Select a group or create a new one</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Group Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" data-testid="create-group-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">New Group Chat</h2>
              <button onClick={() => setShowCreate(false)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <Input data-testid="group-name-input" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name" className="rounded-xl mb-4" />
            <Button data-testid="confirm-create-group-btn" onClick={createGroup} className="w-full bg-coral hover:bg-coral/90 text-white rounded-xl">Create Group</Button>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" data-testid="add-member-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Add Member</h2>
              <button onClick={() => setShowAddMember(false)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search connections..." className="pl-9 rounded-xl" />
            </div>
            <div className="overflow-y-auto flex-1 space-y-2">
              {filteredConnections.map(c => (
                <div key={c.id} className="flex items-center justify-between p-2 rounded-xl hover:bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <Avatar className="w-8 h-8"><AvatarImage src={c.photo} /><AvatarFallback className="bg-teal text-white text-xs">{c.name?.charAt(0)}</AvatarFallback></Avatar>
                    <span className="text-sm font-medium">{c.name}</span>
                  </div>
                  <Button size="sm" onClick={() => addMember(c.id)} className="bg-teal text-white rounded-full text-xs h-7">Add</Button>
                </div>
              ))}
              {filteredConnections.length === 0 && <p className="text-center text-gray-400 py-4 text-sm">No connections to add</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
